import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, IconButton, InputBase, Tooltip, Typography } from "@mui/material";
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import ConfirmDialog from "../Forms/ConfirmDialog";
import { clipboardToPlainText } from "./chatText";

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);
const sendShortcut = isMac ? '⌘ + Entrée' : 'Ctrl + Entrée';

// Drafts are kept per ticket (sessionStorage) so a reply never follows the agent to another ticket
const draftKey = (ticketId) => `sav-chat-draft-${ticketId}`;

const readDraft = (ticketId) => {
  if (!ticketId) return '';
  try {
    return sessionStorage.getItem(draftKey(ticketId)) || '';
  } catch (err) {
    return '';
  }
};

const saveDraft = (ticketId, text) => {
  if (!ticketId) return;
  try {
    if (text) sessionStorage.setItem(draftKey(ticketId), text);
    else sessionStorage.removeItem(draftKey(ticketId));
  } catch (err) {
    // storage unavailable: the draft is simply not kept
  }
};

/**
 * Message composer for SAV agents.
 * - Entrée = nouvelle ligne, Ctrl/⌘ + Entrée = envoyer (after confirmation)
 * - Pasted content (ChatGPT, Word, Gmail…) is converted to clean, structured plain text
 * - Text is kept if sending fails
 */
export default function ChatComposer({ ticketId, ticketLabel, onSend, onAttachClick, attaching, autoFocus }) {
  const inputRef = useRef(null);
  const ticketRef = useRef(ticketId);
  ticketRef.current = ticketId;

  const [draft, setDraft] = useState(() => readDraft(ticketId));
  const [pendingMessage, setPendingMessage] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);

  useEffect(() => {
    setDraft(readDraft(ticketId));
  }, [ticketId]);

  useEffect(() => {
    if (!autoFocus) return undefined;
    // wait for the drawer to be visible before focusing
    const timer = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 150);
    return () => clearTimeout(timer);
  }, [autoFocus]);

  const updateDraft = (text) => {
    setDraft(text);
    saveDraft(ticketId, text);
  };

  const canSend = draft.trim().length > 0 && !sending;

  const submit = () => {
    if (!canSend) return;
    setPendingMessage(draft.trim());
    setShowConfirm(true);
  };

  const confirmSend = async () => {
    if (sendingRef.current) return;
    const sentFor = ticketId;
    sendingRef.current = true;
    setSending(true);
    // the dialog stays open (with a spinner) until the server answers
    const sent = await onSend(pendingMessage);
    sendingRef.current = false;
    setSending(false);
    setShowConfirm(false);
    if (sent) {
      saveDraft(sentFor, '');
      if (ticketRef.current === sentFor) setDraft('');
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  const insertText = (text) => {
    const input = inputRef.current;
    input.focus();
    // execCommand keeps the native undo (Ctrl+Z) working after a paste
    if (document.execCommand && document.execCommand('insertText', false, text)) return;
    const { selectionStart, selectionEnd, value } = input;
    const caret = selectionStart + text.length;
    updateDraft(value.slice(0, selectionStart) + text + value.slice(selectionEnd));
    requestAnimationFrame(() => input.setSelectionRange(caret, caret));
  };

  const handlePaste = (event) => {
    if (sending) return;
    const text = clipboardToPlainText(event.clipboardData);
    if (!text) return;
    event.preventDefault();
    insertText(text);
  };

  return (
    <Box sx={{ flexShrink: 0, p: 1.5, borderTop: '1px solid #EAECF0', backgroundColor: '#FFF' }}>
      <Box
        sx={{
          border: '1px solid #D0D5DD',
          borderRadius: '12px',
          backgroundColor: '#FFF',
          transition: 'border-color .15s ease, box-shadow .15s ease',
          '&:focus-within': {
            borderColor: '#1671D9',
            boxShadow: '0 0 0 3px rgba(22, 113, 217, 0.12)',
          },
        }}
      >
        <InputBase
          inputRef={inputRef}
          multiline
          minRows={2}
          maxRows={10}
          fullWidth
          value={draft}
          readOnly={sending}
          onChange={(event) => updateDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder="Écrivez votre message…"
          inputProps={{ 'aria-label': 'Message', lang: 'fr', spellCheck: true }}
          sx={{
            px: 1.5,
            pt: 1.25,
            pb: 0.5,
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '14px',
            lineHeight: '21px',
            color: '#101828',
            '& textarea': { resize: 'none' },
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, pb: 1 }}>
          <Tooltip title={attaching ? 'Envoi du fichier…' : 'Joindre un fichier ou le glisser dans le chat (PNG, JPG, GIF, PDF, DOC, XLSX — 5 Mo max)'}>
            <span>
              <IconButton size="small" onClick={onAttachClick} disabled={sending || attaching} aria-label="Joindre un fichier">
                {attaching
                  ? <CircularProgress size={18} />
                  : <AttachFileRoundedIcon fontSize="small" sx={{ transform: 'rotate(45deg)' }} />}
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            noWrap
            sx={{ flex: 1, minWidth: 0, fontFamily: '"DM Sans", sans-serif', fontSize: '12px', color: '#98A2B3' }}
          >
            {sendShortcut} pour envoyer
          </Typography>
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={submit}
            disabled={!canSend}
            endIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendRoundedIcon fontSize="small" />}
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              borderRadius: '8px',
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 600,
              backgroundColor: '#1671D9',
            }}
          >
            {sending ? 'Envoi…' : 'Envoyer'}
          </Button>
        </Box>
      </Box>

      <ConfirmDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={confirmSend}
        title="Envoyer ce message ?"
        message={ticketLabel
          ? `Il sera envoyé au client du ticket ${ticketLabel}. Vérifiez-le avant de confirmer.`
          : 'Il sera envoyé au client. Vérifiez-le avant de confirmer.'}
        pendingMessage={pendingMessage}
        confirmLabel="Envoyer le message"
        loading={sending}
      />
    </Box>
  );
}
